// Shared schedule helpers: the density-view range vocabulary and the
// span (from..to) the heatmap renders and the API must fetch. Both
// halves of the contract live here so the heatmap cell grid and the
// useSchedule {from,to} can never drift — DensityHeatmap and
// Schedule.tsx both compute their span via getDensitySpan from the same
// anchor + range.
//
// The type and constant live in /utils (not next to the component) so
// the dependency direction is util ← component, not component ← util ←
// component (which would form a cycle the first time anything else in
// /utils imported DensityRange).

export type DensityRange = "quarter" | "6m" | "12m";

export const RANGE_WEEKS: Record<DensityRange, number> = {
  quarter: 13,
  "6m": 26,
  "12m": 52,
};

const DAY_MS = 86_400_000;

function isoOf(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

// Monday-first weekday index (0 = Mon … 6 = Sun). Mirrors the helper
// inside DensityHeatmap so the cell grid and the API span agree.
function mondayIndex(d: Date): number {
  return (d.getUTCDay() + 6) % 7;
}

// Calendar surface the density heatmap renders for {anchor, range}.
// from = first Monday on or before `anchor`; to = from + RANGE_WEEKS*7
// - 1 day. The result is INCLUSIVE on both ends, which is what
// /manage/schedule wants for its [from, to] window.
export function getDensitySpan(
  anchor: string,
  range: DensityRange,
): { from: string; to: string } {
  const start = new Date(`${anchor}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - mondayIndex(start));
  const days = RANGE_WEEKS[range] * 7;
  const end = new Date(start.getTime() + (days - 1) * DAY_MS);
  return { from: isoOf(start), to: isoOf(end) };
}
